import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { Clock, SendHorizontal, AlertCircle, CheckCircle2, XCircle, Eye, MousePointerClick, Timer } from "lucide-react";
import { UserMessage, MessageStatus } from "@/bt-common/schemas/bt/user-message";
import { isMessageOverdue } from "@/bt-common/services/utilsCommon";

// Effective display status includes "expired" for messages past retry limit,
// "unconfirmed" (#166) for messages handed to WhatsApp with no delivery confirmation
// (sent_pending_ack / legacy sent — see getEffectiveMessageStatus), and "rate_limited" (§2) for a
// pending message the server-side send-slot gate is actively throttling (WhatsApp backoff).
// This config is driven by userMessage docs, so it covers the base statuses + expired + unconfirmed
// + rate_limited.
// "in_transit" (#480) is a still-`pending` doc that already carries a real waMessageKey: the
// engine handed it to WhatsApp and the confirmation just hasn't been recorded yet.
export type EffectiveMessageStatus = MessageStatus | 'expired' | 'unconfirmed' | 'rate_limited' | 'in_transit';

export interface MessageStatusConfig {
  label: string;
  icon: typeof Clock;
  color: string;
  bgColor: string;
  borderColor: string;
  badgeClassName: string;
  description: string;
  iconClassName?: string;
}

/**
 * Single source of truth for message status configuration.
 * Used by both RelativeTimestampBadge (sidebar) and WhatsappMessage (detail view).
 *
 * Note: label and description are i18n translation keys. Components using this config
 * should use t(config.label) and t(config.description) to get translated strings.
 */
// Partial: EffectiveMessageStatus is the full canonical superset (incl. log-only states like
// before-wa-send/bt-sent/delivered/read that a userMessage never actually carries). Only the
// userMessage-reachable statuses need UI config; getStatusConfig() returns null for the rest.
export const MESSAGE_STATUS_CONFIG: Partial<Record<EffectiveMessageStatus, MessageStatusConfig>> = {
  pending: {
    label: "messageStatus.pending.label",
    icon: Clock,
    color: '#53bdeb',
    bgColor: 'transparent',
    borderColor: 'border-l-[#53bdeb]',
    badgeClassName: "bg-[#53bdeb] hover:bg-[#259ae9] text-white border-[#259ae9]",
    description: "messageStatus.pending.description"
  },
  // #166 — "handed to WhatsApp, no delivery confirmation" (sent_pending_ack + legacy
  // sent). Deliberately NO iconClassName: this is background ack reconciliation that
  // routinely lasts weeks (the reaper's 7-day floor means many of these rows are never
  // resolved at all) — a spinner would falsely claim foreground work the user is
  // waiting on. Slate, NOT amber: whatsapp-message.tsx:759 hard-codes
  // border-l-[#f59e0b] (amber-500) for a pending-but-overdue row, so amber here would
  // make "scheduled, late, never sent" and "probably sent, unconfirmed" look identical
  // at the one glance (the left border) users actually use to tell them apart — the two
  // states they most need to distinguish. Grepped the whole row for other hard-coded
  // border-l- values first (whatsapp-message.tsx, this file) to avoid colliding again.
  // Distinct from pending's blue (#53bdeb), failed/expired's red (#ef4444), and the
  // overdue amber (#f59e0b); also reads as WhatsApp's own grey-tick "sent, unconfirmed"
  // vocabulary.
  unconfirmed: {
    label: "messageStatus.unconfirmed.label",
    icon: Clock,
    color: '#64748b',
    bgColor: 'transparent',
    borderColor: 'border-l-slate-500',
    badgeClassName: "bg-slate-500 hover:bg-slate-600 text-white border-slate-600",
    description: "messageStatus.unconfirmed.description"
  },
  // #480 — "handed to WhatsApp, confirmation not recorded yet" for a doc whose status is still
  // `pending` (e.g. firstSendAttempt + retry backoff while WA Web reconnects). Without this it
  // rendered as plain "Scheduled", users re-created the message and the recipient got a real
  // duplicate. Indigo: distinct from pending's blue, unconfirmed's slate, the amber overdue
  // border / rate_limited, and the red failure states. No spinner — the confirmation arrives
  // from the background ack reconciler, not from work the user is waiting on.
  in_transit: {
    label: "messageStatus.inTransit.label",
    icon: SendHorizontal,
    color: '#6366f1',
    bgColor: 'transparent',
    borderColor: 'border-l-indigo-500',
    badgeClassName: "bg-indigo-500 hover:bg-indigo-600 text-white border-indigo-600",
    description: "messageStatus.inTransit.description"
  },
  confirmed: {
    label: "messageStatus.confirmed.label",
    icon: CheckCircle2,
    color: '#10b981',
    bgColor: 'transparent',
    borderColor: 'border-l-emerald-500',
    badgeClassName: "bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600",
    description: "messageStatus.confirmed.description"
  },
  received: {
    label: "messageStatus.received.label",
    icon: CheckCircle2,
    color: '#10b981',
    bgColor: 'transparent',
    borderColor: 'border-l-emerald-500',
    badgeClassName: "bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600",
    description: "messageStatus.received.description"
  },
  read: {
    label: "messageStatus.read.label",
    icon: Eye,
    color: '#2563eb',
    bgColor: 'transparent',
    borderColor: 'border-l-blue-600',
    badgeClassName: "bg-blue-600 hover:bg-blue-700 text-white border-blue-700",
    description: "messageStatus.read.description"
  },
  played: {
    label: "messageStatus.played.label",
    icon: MousePointerClick,
    color: '#9333ea',
    bgColor: 'transparent',
    borderColor: 'border-l-purple-600',
    badgeClassName: "bg-purple-600 hover:bg-purple-700 text-white border-purple-700",
    description: "messageStatus.played.description"
  },
  failed: {
    label: "messageStatus.failed.label",
    icon: AlertCircle,
    color: '#ef4444',
    bgColor: 'transparent',
    borderColor: 'border-l-red-500',
    badgeClassName: "bg-red-500 hover:bg-red-600 text-white border-red-600",
    description: "messageStatus.failed.description"
  },
  expired: {
    label: "messageStatus.expired.label",
    icon: XCircle,
    color: '#ef4444',
    bgColor: 'transparent',
    borderColor: 'border-l-red-500',
    badgeClassName: "bg-red-500 hover:bg-red-600 text-white border-red-600",
    description: "messageStatus.expired.description"
  },
  // §2 — "WhatsApp is throttling this send right now" (the server-authoritative send-slot gate
  // returned a backoff deny; see acquire-send-slot). AMBER, NOT red: the message is still pending
  // and retry-eligible — it will go out once the backoff lifts — so this is a benign "waiting"
  // state, not a failure. No iconClassName: a spinner would falsely claim foreground work; the wait
  // is the server's inter-attempt gap, nothing the user is actively blocked on. Timer glyph +
  // amber-500 (#f59e0b) read as "held, will retry", distinct from pending's blue, failed/expired's
  // red, and unconfirmed's slate. (unconfirmed deliberately avoided amber to stay distinct from the
  // hard-coded overdue-amber border; rate_limited legitimately IS the amber "waiting" state and only
  // ever renders on its own badge, never that border.)
  rate_limited: {
    label: "messageStatus.rateLimited.label",
    icon: Timer,
    color: '#f59e0b',
    bgColor: 'transparent',
    borderColor: 'border-l-amber-500',
    badgeClassName: "bg-amber-500 hover:bg-amber-600 text-white border-amber-600",
    description: "messageStatus.rateLimited.description"
  }
};

/**
 * #141: did this message actually reach WhatsApp? `waMessageKey` is persisted on
 * dispatch (Chrome ext's WhatsappSendMessageThunk) and `ack >= 1` is a real
 * server confirmation — either proves the message was attempted and NOT
 * skipped/expired. Mirrors the backend's "has a send log OR a waMessageKey"
 * heuristic for "was this actually sent" (BE#180).
 */
/**
 * Does the doc carry a REAL WhatsApp message key?
 *
 * Every userMessage the backend creates is pre-populated with an all-null shell —
 * `{_serialized:null, fromMe:null, id:null, remote:null, participant:null}` — and that
 * shell is an object, so a bare `waMessageKey != null` test called every unsent message
 * "dispatched" (16 of 48 messages loaded on a live account). That silently made
 * `rate_limited` unreachable and turned overdue-never-sent rows into
 * 'unconfirmed' — or, for a group, all the way into 'confirmed'. A key counts only when
 * at least one of its identifying fields actually carries a value.
 */
function hasRealWaMessageKey(message: UserMessage): boolean {
  const key = message.waMessageKey as Record<string, unknown> | null | undefined;
  if (key == null) return false;
  if (typeof key !== 'object') return true; // a plain string key (Baileys) is real
  return Object.values(key).some(v => v !== null && v !== undefined && v !== '');
}

function wasDispatchedToWhatsApp(message: UserMessage): boolean {
  if (hasRealWaMessageKey(message)) return true;
  const ack = (message as { ack?: number }).ack;
  return typeof ack === 'number' && ack >= 1;
}

/**
 * FE#249 (facet a): does this message carry a real WhatsApp DELIVERY
 * confirmation? Distinct from `wasDispatchedToWhatsApp` — a `waMessageKey` alone
 * only proves the send reached WhatsApp's store, and a genuine `ack:-1` hard
 * error ALSO has a key. Delivery is proven only by a server ack: `ack >= 1`
 * (scalar wa-js ack), `serverAck >= 1` (persisted), `lastKnownAck >= 1` (the send-time ack
 * persisted from the final send log, #576), or `acknowledged === true`.
 * Used to keep a `failed` doc that was actually delivered (the dual-engine case:
 * another engine delivered the shared message while this one recorded a
 * lock-loss / retry-exhaustion failure) from painting red.
 */
function hasDeliveryConfirmation(message: UserMessage): boolean {
  const ack = (message as { ack?: number }).ack;
  if (typeof ack === 'number' && ack >= 1) return true;
  const serverAck = (message as { serverAck?: number }).serverAck;
  if (typeof serverAck === 'number' && serverAck >= 1) return true;
  // #576: the send-time ack the backend persists from the final send log.
  const lastKnownAck = (message as { lastKnownAck?: number }).lastKnownAck;
  if (typeof lastKnownAck === 'number' && lastKnownAck >= 1) return true;
  return (message as { acknowledged?: boolean }).acknowledged === true;
}

/**
 * FE#172: is this send addressed to a WhatsApp GROUP (`@g.us`)? A group send
 * legitimately has NO per-recipient ack rollup — the scalar `ack` pins at 1
 * (server-received) and only advances to delivered/read once EVERY participant
 * receives, which for any real group effectively never happens. So the
 * "handed off, no delivery confirmation" (unconfirmed) state is EXPECTED for a
 * group, not a problem — flagging it made the badge fire on every group row.
 *
 * The recipient JID is `contactList[0].id` (the engine reads `[0].id` as the WA
 * JID); fall back to the persisted `waMessageKey` remote/serialized form for
 * rows whose contactList wasn't hydrated. Any `@g.us` there → group.
 */
function isGroupSend(message: UserMessage): boolean {
  const contactId = message?.contactList?.[0]?.id;
  if (typeof contactId === 'string' && chatKindOf(contactId) === 'group') return true;

  const key = message.waMessageKey as
    | { remote?: unknown; remoteJid?: unknown; _serialized?: unknown }
    | undefined;
  if (key) {
    const remoteJid = typeof key.remoteJid === 'string' ? key.remoteJid : undefined;
    const remote = typeof key.remote === 'string' ? key.remote : undefined;
    if (remoteJid && chatKindOf(remoteJid) === 'group') return true;
    if (remote && chatKindOf(remote) === 'group') return true;
    // `_serialized` is `fromMe_chatId_msgId[_participant]`; the chat JID is the
    // middle segment(s). A group chat JID itself contains a '-' but no '@' until
    // the suffix, so rejoining the inner segments recovers it.
    if (typeof key._serialized === 'string' && key._serialized.includes('@g.us')) {
      const parts = key._serialized.split('_');
      if (parts.length >= 3 && chatKindOf(parts.slice(1, -1).join('_')) === 'group') return true;
    }
  }
  return false;
}

/**
 * §2 — is this message being throttled by WhatsApp RIGHT NOW? The server-authoritative send-slot
 * gate (acquire-send-slot) stamps `lastSlotReason:'backoff'` + a `backoffUntil` on the doc when it
 * defers a send; `backoffUntil` is the epoch the throttle lifts. "Currently throttled" is precisely
 * a still-`pending` message whose live backoff window has not yet passed. A grant clears
 * `backoffUntil` (leaving `lastSlotReason` sticky for the failure-cause read), so a past/absent
 * `backoffUntil` is NOT currently throttled — it either already went out or is between attempts.
 */
function isCurrentlyRateLimited(message: UserMessage, now: number = Date.now()): boolean {
  const anyM = message as { lastSlotReason?: string; backoffUntil?: Date | string | null };
  if (anyM.lastSlotReason !== 'backoff' || anyM.backoffUntil == null) return false;
  const until = anyM.backoffUntil instanceof Date ? anyM.backoffUntil : new Date(anyM.backoffUntil);
  return !Number.isNaN(until.getTime()) && until.getTime() > now;
}

/**
 * Computes the effective display status for a message.
 * If a message is still "pending" but past the retry limit (isMessageOverdue),
 * it should be displayed as "expired" since it won't be retried automatically —
 * unless it was actually dispatched (see wasDispatchedToWhatsApp, #141).
 */
export function getEffectiveMessageStatus(message: UserMessage | undefined | null): EffectiveMessageStatus | undefined {
  if (!message || !message.status) return undefined;

  const dispatched = wasDispatchedToWhatsApp(message);

  // §2 — a still-pending send that WhatsApp is actively throttling. Checked before the overdue /
  // handed-off branches, gated on `!dispatched`: a message the slot deferred never reached WhatsApp
  // (no waMessageKey / ack), so a dispatched doc with a stale backoff marker falls through to the
  // real handed-off logic below rather than showing a phantom "rate limited".
  if (message.status === 'pending' && !dispatched && isCurrentlyRateLimited(message)) {
    return 'rate_limited';
  }

  // FE#172: a dispatched GROUP send is as confirmed as a group can be. Its
  // scalar ack pins at server-received (1) and never rolls up to a per-recipient
  // delivered/read across all participants, so "unconfirmed" is a false alarm on
  // every group row. Resolve a dispatched group send to 'confirmed' instead of
  // 'unconfirmed'; a 1:1 send (or a group send with no dispatch evidence) is
  // unchanged. Only ever downgrades the *unconfirmed* branches below — never
  // touches a genuine 'failed'/'expired-never-sent'.
  // #576: only once that server ack (>= 1) is actually known. A key alone is not
  // enough: a group send whose ack stayed 0 (PENDING) never reached WhatsApp's
  // server, and painting it green hid 7 undelivered sends from a customer.
  const handedOffStatus = (): EffectiveMessageStatus =>
    dispatched && isGroupSend(message) && hasDeliveryConfirmation(message) ? 'confirmed' : 'unconfirmed';

  // #141: 'expired' means "max-send-window elapsed before send; never attempted".
  // A message that carries send-evidence (a persisted waMessageKey / a real ack)
  // WAS attempted and reached WhatsApp, so an 'expired' flag written from an
  // incomplete signal (max-send-window skip / abandoned reaper) is stale and
  // would mislead the customer into thinking the send failed. Show the honest
  // "handed off, no confirmation" state instead — never 'expired'.
  if (message.status === 'expired' && dispatched) {
    return handedOffStatus();
  }

  // If still pending but past the retry limit, show as "expired" — UNLESS the
  // message was actually dispatched (its status doc just never advanced past
  // 'pending'), in which case it's 'unconfirmed', not a never-sent skip (#141).
  if (message.status === 'pending' && isMessageOverdue(message)) {
    return dispatched ? handedOffStatus() : 'expired';
  }

  // #480 — still `pending` (not overdue) but the doc already carries a REAL waMessageKey /
  // ack: the message is in WhatsApp's hands, only the confirmation is outstanding. Show
  // "Sending · waiting for WhatsApp", never "Scheduled". Keyed on real send evidence
  // (wasDispatchedToWhatsApp), NOT firstSendAttempt: that is stamped on any attempt, including
  // one that threw before WhatsApp accepted it. Groups too: not overdue means retry is still
  // possible, so we don't claim 'confirmed' on a key alone.
  // A requeued doc can keep its key with ack -1 (a hard send error): that did NOT reach
  // WhatsApp, so it stays pending and keeps Send-now.
  if (message.status === 'pending' && dispatched && (message as { ack?: number }).ack !== -1) {
    return 'in_transit';
  }

  // #166 — "handed to WhatsApp, no delivery confirmation". `sent_pending_ack`
  // has a real waMessageKey but ack 0; legacy `sent` has no key at all. Neither
  // has confirmation, so both get the same honest state. NOT 'failed' (we don't
  // know that) and NOT 'confirmed' (we don't know that either) — except a
  // dispatched group send with a known server ack, which resolves to 'confirmed'
  // (FE#172 + #576, above).
  if (message.status === 'sent_pending_ack' || message.status === 'sent') {
    return dispatched ? handedOffStatus() : 'unconfirmed';
  }

  // FE#249 (facet a) — a `failed` doc that nonetheless carries a real DELIVERY
  // confirmation WAS delivered and must never paint red. This is the dual-engine
  // display race: the Gateway wins the send-lock and delivers the shared message
  // (ack >= 1) while the extension's losing attempt / the abandoned reaper marks
  // the SAME userMessage `failed`. Guarded on delivery evidence, NOT bare
  // dispatch: a genuine WhatsApp `ack:-1` send error also has a waMessageKey and
  // must stay 'failed'. (Doc-level only — the durable cross-engine reconciliation
  // that writes delivery back onto the doc is the Tier-2 backend fix.)
  if (message.status === 'failed' && hasDeliveryConfirmation(message)) {
    return 'confirmed';
  }

  return message.status;
}

/**
 * Get the status configuration for a given effective status.
 */
export function getStatusConfig(status: EffectiveMessageStatus | undefined): MessageStatusConfig | null {
  if (!status) return null;
  return MESSAGE_STATUS_CONFIG[status] || null;
}

/** The curated backend failure code (§1: acquire-send-slot's exhausted path) marking a terminal
 *  failure whose cause was repeated WhatsApp backoff. Distinct from a generic send failure. */
export const RATE_LIMITED_FAILURE_REASON = 'rate_limited';

/**
 * §2 — is this a terminal failure caused by WhatsApp rate-limiting (vs a generic failure)? True only
 * when the message's EFFECTIVE status is 'failed' (so a `failed` doc that actually carries delivery
 * evidence — downgraded to 'confirmed' by FE#249 — is correctly NOT flagged) AND the backend stamped
 * the curated `sendFailureReason:'rate_limited'`. Lets the badge render "Failed — WhatsApp rate
 * limit" instead of the generic failed label.
 */
export function isRateLimitedFailure(message: UserMessage | undefined | null): boolean {
  if (!message) return false;
  return (
    getEffectiveMessageStatus(message) === 'failed' &&
    (message as { sendFailureReason?: string }).sendFailureReason === RATE_LIMITED_FAILURE_REASON
  );
}

/** The curated backend failure code (BE#710) marking a terminal failure whose cause was
 *  "the recipient number is not registered on WhatsApp". Mirrors RATE_LIMITED_FAILURE_REASON. */
export const NOT_ON_WHATSAPP_FAILURE_REASON = 'not_on_whatsapp';

/**
 * Legacy rows (pre-BE#710) carry only the RAW engine string in `sendFailureError`/
 * `sendFailureReason`. This matches the three persisted phrasings —
 * "Phone number / Chat ID / Recipient is not on WhatsApp".
 *
 * Deliberately does NOT match:
 *  - "No LID for user (…)" — BE ruling: Chrome-generic + Baileys-transient, not provably
 *    not-on-WhatsApp (so it must stay a generic failure);
 *  - a bare "not found" — store-desync trap;
 *  - rate_limited / account-restriction markers.
 * None of those contain the substring "is not on WhatsApp", so a single positive pattern
 * (no negative lookahead needed) is both sufficient and safe.
 */
const NOT_ON_WHATSAPP_TEXT = /is not on WhatsApp/i;

/** The raw, human-readable failure text for matching: `sendFailureReason` + any string
 *  `sendFailureError` (or its `.message` when the error is an object). */
function failureTextOf(message: UserMessage | undefined | null): string {
  if (!message) return '';
  const m = message as { sendFailureReason?: unknown; sendFailureError?: unknown };
  const parts: string[] = [];
  if (typeof m.sendFailureReason === 'string') parts.push(m.sendFailureReason);
  const err = m.sendFailureError;
  if (typeof err === 'string') parts.push(err);
  else if (err && typeof err === 'object') {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === 'string') parts.push(msg);
  }
  return parts.join(' ');
}

/**
 * Is this a terminal failure caused by the recipient not being on WhatsApp?
 *
 * Mirrors the backend matcher (BE#710) EXACTLY: TRUE when the message's EFFECTIVE status is
 * `failed` (so a delivered `failed` doc downgraded to 'confirmed' by FE#249 is correctly NOT
 * flagged) AND either the curated `sendFailureReason:'not_on_whatsapp'` code is present (new
 * rows) OR the legacy raw text matches NOT_ON_WHATSAPP_TEXT (historical rows, incl. Otef's).
 * "No LID for user", bare "not found", rate_limited and restriction never match (see the regex).
 */
export function isNotOnWhatsAppFailure(message: UserMessage | undefined | null): boolean {
  if (!message) return false;
  if (getEffectiveMessageStatus(message) !== 'failed') return false;
  if ((message as { sendFailureReason?: string }).sendFailureReason === NOT_ON_WHATSAPP_FAILURE_REASON) {
    return true;
  }
  return NOT_ON_WHATSAPP_TEXT.test(failureTextOf(message));
}

/** What a failed message says about WHY — the same words wherever a failed send is shown
 *  (chat bubble, calendar hover card): "not on WhatsApp", else the customer text the backend wrote,
 *  else the generic explanation. Never empty. */
export function describeSendFailure(message: UserMessage | undefined | null, t: (key: string) => string): string {
  if (isNotOnWhatsAppFailure(message)) return t('messageStatus.failedNotOnWhatsApp.description');
  return stripSendFailureInternals(message?.sendFailureError)
    || t('whatsappMessage.failedToSendDescription');
}

/**
 * Strip the internal diagnostic suffix the engine appends to a raw failure string —
 * e.g. "No LID for user (userMessageId=…, chatId=…)" → "No LID for user". These
 * ids/secrets are forensic only and must never reach a customer-facing surface.
 * Non-string input (object errors) yields an empty string.
 */
export function stripSendFailureInternals(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw.replace(/\s*\((?:userMessageId|chatId|secret)=[\s\S]*$/i, '').trim();
}

/**
 * §2 — the i18n LABEL key to render for a message, accounting for the failure variants.
 * The shared resolver the calendar + WhatsApp-UI badges use so the "Failed — …" copy lives in
 * one place. Falls back to the effective-status config label; returns the generic failed label
 * for a `failed` message without a curated reason.
 *
 * BE#710 (conductor ruling): the per-row status PILL stays the baseline "Failed" for a
 * not-on-WhatsApp failure — the not-on-WhatsApp detail is surfaced via the row hover tooltip,
 * the campaign banner, the "View these numbers" filter and the summary chip, NOT the pill. So
 * this resolver deliberately does NOT special-case isNotOnWhatsAppFailure (reverted); the rate-
 * limit variant keeps its own label.
 */
export function getStatusLabelKey(message: UserMessage | undefined | null): string {
  if (isRateLimitedFailure(message)) return 'messageStatus.failedRateLimited.label';
  const config = getStatusConfig(getEffectiveMessageStatus(message ?? undefined));
  return config?.label ?? 'messageStatus.failed.label';
}

// Mirrors the backend's ACK_RECOVERABLE_HORIZON_MS (abandoned-message.ts): inside this
// window the reconciler may still promote sent_pending_ack -> confirmed, so the tooltip
// should not yet imply the wait is unusual.
const UNCONFIRMED_TOOLTIP_RECENT_HORIZON_MS = 48 * 60 * 60 * 1000;

export interface UnconfirmedTooltipInfo {
  /** i18n key to pass to t(); params (if any) go alongside it. */
  key: string;
  params?: { days: number };
}

/**
 * #166 rev2 — the "unconfirmed" tooltip branches on how long ago the message was
 * actually handed to WhatsApp, using `lastSendAttempt` (stamped per real send
 * attempt) rather than `dueDate` (which rolls forward on recurring messages — a
 * STILL-unconfirmed row can show a `dueDate` from a later occurrence than the one
 * that actually sent; see MEMORY: doc reused across occurrences).
 *
 * - `lastSendAttempt` absent -> fall back to the single, age-agnostic tooltip.
 * - within 48h -> "waiting for confirmation" (still inside the ack-recoverable horizon).
 * - older -> "N days ago; may still have been delivered".
 *
 * Pure and framework-free so it's unit-testable without the render harness
 * whatsapp-message.tsx doesn't have (see messageStatusConfig.test.ts).
 */
export function getUnconfirmedTooltipInfo(
  message: { lastSendAttempt?: Date | string | null } | undefined | null,
  now: Date = new Date(),
): UnconfirmedTooltipInfo {
  const lastSendAttempt = message?.lastSendAttempt;
  if (!lastSendAttempt) {
    return { key: 'messageStatus.unconfirmed.tooltip' };
  }

  const attemptDate = lastSendAttempt instanceof Date ? lastSendAttempt : new Date(lastSendAttempt);
  if (Number.isNaN(attemptDate.getTime())) {
    return { key: 'messageStatus.unconfirmed.tooltip' };
  }

  const ageMs = now.getTime() - attemptDate.getTime();
  if (ageMs <= UNCONFIRMED_TOOLTIP_RECENT_HORIZON_MS) {
    return { key: 'messageStatus.unconfirmed.tooltipRecent' };
  }

  const days = Math.floor(ageMs / (24 * 60 * 60 * 1000));
  return { key: 'messageStatus.unconfirmed.tooltipStale', params: { days } };
}

/**
 * Can the user manually (re-)send this message from a row action (the
 * Send-now / retry button)?
 *
 * #94/#98: a `sent_pending_ack` message already reached WhatsApp with a real
 * `waMessageKey` — offering manual retry on it is a one-click duplicate-send.
 * #166: legacy `sent` messages (no delivery key at all) are the same "already
 * handed off, unconfirmed" case and get the same gate — both resolve to the
 * `'unconfirmed'` effective status excluded below.
 * This is the single source of truth for that gate (whatsapp-message.tsx
 * consumes it directly) so a future edit to the JSX can't silently drop the
 * guard without a test noticing — see message-status-config.test.ts.
 */
export function canManuallySend(message: UserMessage | undefined): boolean {
  // #94/#98 + FE#172: a message already handed to WhatsApp with a real
  // waMessageKey must NEVER offer manual (re)send — one click duplicates it.
  // This guard is bound to the raw handed-off statuses, NOT the effective badge,
  // because FE#172 now relabels a dispatched GROUP send as 'confirmed' (its
  // scalar ack legitimately pins at server-received); without this the group's
  // Send-now button would re-enable and reintroduce the #98 duplicate send.
  if (
    message &&
    wasDispatchedToWhatsApp(message) &&
    (message.status === 'sent_pending_ack' || message.status === 'sent')
  ) {
    return false;
  }
  const effectiveStatus = getEffectiveMessageStatus(message);
  // #480: 'in_transit' (pending + real waMessageKey) is already in WhatsApp's hands — a manual
  // send would be the very duplicate the issue describes.
  return effectiveStatus !== 'expired' && effectiveStatus !== 'failed'
    && effectiveStatus !== 'unconfirmed' && effectiveStatus !== 'in_transit';
}
